import React from "react";
import {
  Box,
  VStack,
  HStack,
  Card,
  CardBody,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Progress,
  Badge,
  Flex,
  Divider,
} from "@chakra-ui/react";
import { 
  FiTrendingUp, 
  FiTrendingDown, 
  FiUsers, 
  FiClock, 
  FiDollarSign,
  FiAlertTriangle,
  FiCheckCircle
} from "react-icons/fi";
import { useOvertimeAnalytics, overtimeManagementUtils } from "../../../services/adminOvertimeService";

const OvertimeAnalytics = () => {
  // Load analytics data
  const {
    analyticsData,
    analyticsError,
    analyticsIsLoading,
    refetchAnalytics
  } = useOvertimeAnalytics();

  if (analyticsIsLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4}>Loading overtime analytics...</Text>
      </Box>
    );
  }

  if (analyticsError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Error Loading Analytics</AlertTitle>
        <AlertDescription>
          {analyticsError.message || "Failed to load overtime analytics. Please try again."}
        </AlertDescription>
      </Alert>
    );
  }

  // Extract data from analytics response
  const data = analyticsData || {};
  const {
    total_pending_requests = 0,
    total_approved_requests = 0,
    total_rejected_requests = 0,
    total_overtime_hours = 0,
    average_overtime_per_employee = 0,
    weekly_trend = [],
    monthly_trend = []
  } = data;

  const totalRequests = total_pending_requests + total_approved_requests + total_rejected_requests;
  const approvalRate = totalRequests > 0 ? ((total_approved_requests / totalRequests) * 100).toFixed(1) : 0;

  return (
    <VStack spacing={6} align="stretch">
      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <Card>
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg="blue.100"
                  color="blue.600"
                >
                  <FiUsers size={20} />
                </Box>
                <StatLabel color="gray.600" fontSize="sm">Total Requests</StatLabel>
              </HStack>
              <StatNumber fontSize="2xl" fontWeight="700" color="gray.900">
                {totalRequests}
              </StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                All time requests
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg="yellow.100"
                  color="yellow.600"
                >
                  <FiClock size={20} />
                </Box>
                <StatLabel color="gray.600" fontSize="sm">Pending Requests</StatLabel>
              </HStack>
              <StatNumber fontSize="2xl" fontWeight="700" color="gray.900">
                {total_pending_requests}
              </StatNumber>
              <StatHelpText>
                Awaiting approval
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg="green.100"
                  color="green.600"
                >
                  <FiCheckCircle size={20} />
                </Box>
                <StatLabel color="gray.600" fontSize="sm">Approval Rate</StatLabel>
              </HStack>
              <StatNumber fontSize="2xl" fontWeight="700" color="gray.900">
                {approvalRate}%
              </StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                {total_approved_requests} approved
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg="red.100"
                  color="red.600"
                >
                  <FiDollarSign size={20} />
                </Box>
                <StatLabel color="gray.600" fontSize="sm">Total Hours</StatLabel>
              </HStack>
              <StatNumber fontSize="2xl" fontWeight="700" color="gray.900">
                {overtimeManagementUtils.formatOvertimeHours(total_overtime_hours)}
              </StatNumber>
              <StatHelpText>
                Approved overtime
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Request Status Breakdown */}
      <Card>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="600" color="gray.800">
              Request Status Breakdown
            </Text>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600">Pending</Text>
                  <Badge colorScheme="yellow" variant="subtle">
                    {total_pending_requests}
                  </Badge>
                </HStack>
                <Progress 
                  value={totalRequests > 0 ? (total_pending_requests / totalRequests) * 100 : 0} 
                  colorScheme="yellow" 
                  size="sm" 
                  borderRadius="full"
                />
              </Box>
              
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600">Approved</Text>
                  <Badge colorScheme="green" variant="subtle">
                    {total_approved_requests}
                  </Badge>
                </HStack>
                <Progress 
                  value={totalRequests > 0 ? (total_approved_requests / totalRequests) * 100 : 0} 
                  colorScheme="green" 
                  size="sm" 
                  borderRadius="full"
                />
              </Box>
              
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600">Rejected</Text>
                  <Badge colorScheme="red" variant="subtle">
                    {total_rejected_requests}
                  </Badge>
                </HStack>
                <Progress 
                  value={totalRequests > 0 ? (total_rejected_requests / totalRequests) * 100 : 0} 
                  colorScheme="red" 
                  size="sm" 
                  borderRadius="full"
                />
              </Box>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      {/* Average Overtime Per Employee */}
      <Card>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="600" color="gray.800">
              Average Overtime Per Employee
            </Text>
            
            <Box textAlign="center" py={4}>
              <Text fontSize="3xl" fontWeight="700" color="blue.600">
                {average_overtime_per_employee.toFixed(1)}h
              </Text>
              <Text fontSize="sm" color="gray.500">
                Average overtime hours per employee this month
              </Text>
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* Weekly Trend */}
      {weekly_trend && weekly_trend.length > 0 && (
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="600" color="gray.800">
                Weekly Overtime Trend
              </Text>
              
              <VStack spacing={3} align="stretch">
                {weekly_trend.map((week, index) => (
                  <Box key={index}>
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" fontWeight="500">
                        Week {week.week_number || index + 1}
                      </Text>
                      <Text fontSize="sm" color="blue.600" fontWeight="600">
                        {overtimeManagementUtils.formatOvertimeHours(week.hours || 0)}
                      </Text>
                    </HStack>
                    <Progress 
                      value={Math.min((week.hours || 0) / 40 * 100, 100)} 
                      colorScheme="blue" 
                      size="sm" 
                      borderRadius="full"
                    />
                  </Box>
                ))}
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Monthly Trend */}
      {monthly_trend && monthly_trend.length > 0 && (
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="600" color="gray.800">
                Monthly Overtime Trend
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {monthly_trend.map((month, index) => (
                  <Box key={index} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
                    <VStack spacing={2} align="stretch">
                      <Text fontSize="sm" fontWeight="600" color="gray.700">
                        {month.month_name || `Month ${index + 1}`}
                      </Text>
                      <Text fontSize="lg" fontWeight="700" color="blue.600">
                        {overtimeManagementUtils.formatOvertimeHours(month.hours || 0)}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {month.requests || 0} requests
                      </Text>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Insights */}
      <Card>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="600" color="gray.800">
              Key Insights
            </Text>
            
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} bg="blue.50" borderRadius="md">
                <HStack spacing={3} mb={2}>
                  <FiTrendingUp size={16} color="blue.600" />
                  <Text fontSize="sm" fontWeight="600" color="blue.800">
                    High Approval Rate
                  </Text>
                </HStack>
                <Text fontSize="sm" color="blue.700">
                  {approvalRate}% of overtime requests are approved, indicating good planning and justification.
                </Text>
              </Box>
              
              <Box p={4} bg="yellow.50" borderRadius="md">
                <HStack spacing={3} mb={2}>
                  <FiAlertTriangle size={16} color="orange.600" />
                  <Text fontSize="sm" fontWeight="600" color="orange.800">
                    Pending Requests
                  </Text>
                </HStack>
                <Text fontSize="sm" color="orange.700">
                  {total_pending_requests} requests awaiting approval. Consider reviewing them promptly.
                </Text>
              </Box>
              
              <Box p={4} bg="green.50" borderRadius="md">
                <HStack spacing={3} mb={2}>
                  <FiClock size={16} color="green.600" />
                  <Text fontSize="sm" fontWeight="600" color="green.800">
                    Total Overtime Hours
                  </Text>
                </HStack>
                <Text fontSize="sm" color="green.700">
                  {overtimeManagementUtils.formatOvertimeHours(total_overtime_hours)} total approved overtime hours.
                </Text>
              </Box>
              
              <Box p={4} bg="purple.50" borderRadius="md">
                <HStack spacing={3} mb={2}>
                  <FiUsers size={16} color="purple.600" />
                  <Text fontSize="sm" fontWeight="600" color="purple.800">
                    Average Per Employee
                  </Text>
                </HStack>
                <Text fontSize="sm" color="purple.700">
                  Average of {average_overtime_per_employee.toFixed(1)} hours per employee this month.
                </Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default OvertimeAnalytics; 